import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import QuickAddItemDialog from './QuickAddItemDialog';

// Link an existing (issued/received) PO line to an inventory item; optionally back-post already-received qty to stock.
export const LinkPoLineDialog = ({ api, open, onOpenChange, poId, poNumber, vendorId, line, items, onLinked }) => {
  const [list, setList] = useState(items || []);
  const [q, setQ] = useState('');
  const [itemId, setItemId] = useState('');
  const [post, setPost] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  useEffect(() => { if (open) { setQ(''); setItemId(''); setPost(true); } }, [open]);
  useEffect(() => { if (items?.length) setList(items); else if (open) api().get('/items').then(r => setList(r.data || [])).catch(() => {}); }, [open, items]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (s ? list.filter(x => `${x.item_sku} ${x.item_name} ${x.vendor_sku || ''}`.toLowerCase().includes(s)) : list).slice(0, 200);
  }, [list, q]);
  if (!line) return null;
  const received = Number(line.received_qty || 0);

  const submit = async () => {
    if (!itemId) { toast.error('Pick an item'); return; }
    setBusy(true);
    try {
      const r = await api().post(`/purchase-orders/${poId}/lines/${line.id}/link`, { item_id: itemId, post_received: post });
      toast.success(r.data.posted_qty ? `Linked and posted ${r.data.posted_qty} to stock` : 'Line linked to inventory');
      onLinked(r.data.po); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open && !newOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="link-po-line-dialog">
          <DialogHeader><DialogTitle>Link line to inventory{poNumber ? ` · ${poNumber}` : ''}</DialogTitle></DialogHeader>
          <div className="text-sm rounded-sm bg-slate-50 border border-slate-200 p-2" data-testid="link-line-summary">
            <div className="font-medium">{line.description || '(no description)'}</div>
            <div className="text-xs text-slate-500">{line.vendor_part_number ? `Vendor P/N ${line.vendor_part_number} · ` : ''}Qty {line.quantity} · received {received}</div>
          </div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, name or vendor part #" className="rounded-sm" data-testid="link-line-search" />
          <select size={8} value={itemId} onChange={(e) => setItemId(e.target.value)} className="w-full border border-slate-200 rounded-sm text-sm p-1" data-testid="link-line-item-select">
            {shown.map(x => <option key={x.id} value={x.id}>{x.item_sku} · {x.item_name}</option>)}
          </select>
          <Button variant="outline" size="sm" className="rounded-sm self-start" onClick={() => setNewOpen(true)} data-testid="link-line-create-item-btn">+ Create a new item from this line</Button>
          {received > 0 && (
            <label className="flex items-start gap-2 text-sm" data-testid="link-line-post-received">
              <input type="checkbox" checked={post} onChange={(e) => setPost(e.target.checked)} className="mt-1" data-testid="link-line-post-checkbox" />
              <span>Add the {received} already received to stock now (restock at this PO's unit cost). Leave unchecked if the stock was already counted another way.</span>
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="link-line-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !itemId} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="link-line-submit">{busy ? 'Linking…' : 'Link item'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuickAddItemDialog api={api} open={open && newOpen} onOpenChange={setNewOpen} vendorId={vendorId} seed={line}
        onCreated={(it) => { setList(l => [it, ...l]); setItemId(it.id); setQ(it.item_sku); }} />
    </>
  );
};

export default LinkPoLineDialog;
